import { useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { clean, num } from '../utils/format';
import { useIsEditing, useRawProperty } from '../utils/hooks';
import { feeFacts, hasFees } from '../utils/fees';

const photo1Def: SlotGroupDefinition = {
  label: 'Hero Photo 1',
  description: 'Defaults to gallery photo 1. Empty photos are skipped in the carousel.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'The single most attractive photo of this property: its exterior, grounds, a shared amenity space, or a furnished living area or kitchen. Never a bathroom, toilet, closet, laundry or utility room, bare unfinished room, floor plan drawing, map, logo, or text graphic.',
    context: 'Slide 1 of up to 10 in the Floor Plans page hero carousel (large landscape photo beside the headline). Shown first on load. Do not reuse a photo from slides 2–10.',
  },
};

const photo2Def: SlotGroupDefinition = {
  label: 'Hero Photo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'A second flattering photo of this property that shows a different view or space from slide 1. Never a bathroom, toilet, closet, laundry or utility room, bare unfinished room, floor plan drawing, map, logo, or text graphic.',
    context: 'Slide 2 of up to 10 in the Floor Plans page hero carousel (large landscape photo). Different photo from every other slide.',
  },
};

const photo3Def: SlotGroupDefinition = {
  label: 'Hero Photo 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'A third flattering photo of this property, ideally an inside-the-home view if slides 1–2 are outdoors (or the reverse). Never a bathroom, toilet, closet, laundry or utility room, bare unfinished room, floor plan drawing, map, logo, or text graphic.',
    context: 'Slide 3 of up to 10 in the Floor Plans page hero carousel (large landscape photo). Different photo from every other slide.',
  },
};

const photo4Def: SlotGroupDefinition = {
  label: 'Hero Photo 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'Another unused, well-composed photo of this property worth showing a prospective renter. Never a bathroom, toilet, closet, laundry or utility room, bare unfinished room, floor plan drawing, map, logo, or text graphic.',
    context: 'Slide 4 of up to 10 in the Floor Plans page hero carousel (large landscape photo). Different photo from every other slide.',
  },
};

const photo5Def: SlotGroupDefinition = {
  label: 'Hero Photo 5',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 4, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'Another unused, well-composed photo of this property, or leave empty if no remaining photo is as strong as slides 1–4. Never a bathroom, toilet, closet, laundry or utility room, bare unfinished room, floor plan drawing, map, logo, or text graphic.',
    context: 'Slide 5 of up to 10 in the Floor Plans page hero carousel (large landscape photo). Optional; different photo from every other slide.',
  },
};

const photo6Def: SlotGroupDefinition = {
  label: 'Hero Photo 6',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 5, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'An unused attractive photo of this property, or leave empty if none is left. Never a bathroom, toilet, closet, laundry or utility room, bare unfinished room, floor plan drawing, map, logo, or text graphic.',
    context: 'Slide 6 of up to 10 in the Floor Plans page hero carousel (large landscape photo). Optional; different photo from every other slide.',
  },
};

const photo7Def: SlotGroupDefinition = {
  label: 'Hero Photo 7',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[6].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[6].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 6, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'A seventh distinct attractive photo of this property, or leave empty if none is left. Never a bathroom, toilet, closet, laundry or utility room, bare unfinished room, floor plan drawing, map, logo, or text graphic.',
    context: 'Slide 7 of up to 10 in the Floor Plans page hero carousel (large landscape photo). Optional; different photo from every other slide.',
  },
};

const photo8Def: SlotGroupDefinition = {
  label: 'Hero Photo 8',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[7].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[7].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 7, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'An eighth distinct attractive photo of this property, or leave empty if none is left. Never a bathroom, toilet, closet, laundry or utility room, bare unfinished room, floor plan drawing, map, logo, or text graphic.',
    context: 'Slide 8 of up to 10 in the Floor Plans page hero carousel (large landscape photo). Optional; different photo from every other slide.',
  },
};

const photo9Def: SlotGroupDefinition = {
  label: 'Hero Photo 9',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[8].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[8].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 8, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'A ninth distinct attractive photo of this property, or leave empty if none is left. Never a bathroom, toilet, closet, laundry or utility room, bare unfinished room, floor plan drawing, map, logo, or text graphic.',
    context: 'Slide 9 of up to 10 in the Floor Plans page hero carousel (large landscape photo). Optional; different photo from every other slide.',
  },
};

const photo10Def: SlotGroupDefinition = {
  label: 'Hero Photo 10',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[9].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[9].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 9, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'A last distinct attractive photo of this property, or leave empty if none is left. Never a bathroom, toilet, closet, laundry or utility room, bare unfinished room, floor plan drawing, map, logo, or text graphic.',
    context: 'Slide 10 of 10 (the final slide) in the Floor Plans page hero carousel (large landscape photo). Optional; different photo from every other slide.',
  },
};

interface Offer {
  enabled?: string | boolean;
}

/** No plan and no property count reports an available home; the availability line greys out. */
function noneAvailable(p: Record<string, unknown>): boolean {
  const plans = (p.floorplans as Array<{ floorplan_available_units?: string }>) || [];
  const planAvail = plans.reduce((s, f) => s + (num(clean(f.floorplan_available_units)) || 0), 0);
  return plans.length > 0 && !planAvail && !num(p.property_available_units);
}

export function FloorPlansHero() {
  const p = useRawProperty();
  const editing = useIsEditing();
  const groups = [
    useSlotGroup('floorplanshero.photo_1', photo1Def),
    useSlotGroup('floorplanshero.photo_2', photo2Def),
    useSlotGroup('floorplanshero.photo_3', photo3Def),
    useSlotGroup('floorplanshero.photo_4', photo4Def),
    useSlotGroup('floorplanshero.photo_5', photo5Def),
    useSlotGroup('floorplanshero.photo_6', photo6Def),
    useSlotGroup('floorplanshero.photo_7', photo7Def),
    useSlotGroup('floorplanshero.photo_8', photo8Def),
    useSlotGroup('floorplanshero.photo_9', photo9Def),
    useSlotGroup('floorplanshero.photo_10', photo10Def),
  ];
  const [i, setI] = useState(0);
  const x0 = useRef<number | null>(null);

  const name = clean(p.property_name);
  const fallbackAlt = name ? `Photo of ${name}` : '';
  /** Live, empty slots drop out of the carousel; in the editor they stay as placeholders so each slot can be filled. */
  const photos = groups
    .map((g, k) => ({ slot: k as number | null, src: clean(g.image) || '', alt: clean(g.alt) || fallbackAlt }))
    .filter((g) => editing || g.src);
  const featured = clean(p.property_featured_image);
  const slides = photos.length ? photos : featured ? [{ slot: null, src: featured, alt: fallbackAlt }] : [];
  const n = slides.length;
  const cur = n ? ((i % n) + n) % n : 0;
  const go = (d: number) => setI((v) => (((v + d) % n) + n) % n);
  const active = n ? slides[cur] : null;

  const slideImage = (src: string, alt: string) => (
    <VibesitesImage
      key={src}
      url={src}
      alt={alt}
      priority={cur === 0}
      sizes="(min-width: 1200px) 45vw, 100vw"
      className="absolute inset-0 h-full w-full animate-[fade_var(--q-motion-normal)_var(--q-ease-in-out)] object-cover"
    />
  );
  const slide = (k: number) => {
    if (!active || active.slot !== k) return null;
    if (active.src) return slideImage(active.src, active.alt);
    return (
      <div className="flex h-full items-center justify-center border border-dashed border-[--q-border] text-sm text-muted-foreground">
        Hero Photo {k + 1} · empty, hidden on the live site
      </div>
    );
  };
  const slotClass = (k: number) => (active?.slot === k ? 'h-full' : '');

  const none = noneAvailable(p);
  const offer = ((p.special_offers as Offer[]) || []).some((o) => String(o.enabled) === 'true');
  const fees = feeFacts(p);
  const links = [
    ((p.floorplans as unknown[]) || []).length > 0 && { label: 'Compare Plans', short: 'Plans', href: '#compare-plans' },
    hasFees(fees) && { label: 'Fees & Deposits', short: 'Fees', href: '#fees' },
    !!p.property_ispetfriendly && { label: 'Pets', short: 'Pets', href: '#pets' },
    { label: 'Tour & Apply', short: 'Tour', href: '#tour' },
  ].filter((l): l is { label: string; short: string; href: string } => !!l);

  return (
    <section
      className="page-hero min-h-[610px] [&.page-hero]:pb-10 [&.page-hero]:pt-12 max-[1199px]:min-h-0 max-[767px]:[&.page-hero]:pt-10"
    >
      <div
        className={`page-hero__copy [&&]:gap-[22px] max-[767px]:[&&]:gap-4 ${
          n ? '[&&]:w-[560px] max-[1199px]:[&&]:w-auto' : 'max-w-[760px] [&&]:w-auto'
        }`}
        data-reveal
      >
        <RichTextContent path="floorplanshero.eyebrow" as="div" className="eyebrow" />
        <RichTextContent path="floorplanshero.title" as="h1" className="h1" />
        <div className="flex flex-col items-start gap-3.5">
          <RichTextContent
            path="floorplanshero.summary"
            as="p"
            className={`m-0 text-base leading-6 text-muted-foreground has-[>[data-vibesite-richtext]:empty]:hidden max-[767px]:text-[15px] max-[767px]:leading-[23px]${
              none ? " before:mr-2 before:text-muted-foreground before:content-['●']" : ''
            }`}
          />
          {(offer || editing) && (
            <button
              className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-[--q-offer] px-4 py-0 text-[13px] font-medium leading-[18px] text-[--q-on-offer] [transition:transform_var(--q-motion-normal)_var(--q-ease-out)] [&_p]:m-0 [&_p]:inline hover:[transform:translateY(-1px)] motion-reduce:hover:[transform:none]"
              type="button"
              onClick={() => window.dispatchEvent(new Event('site:open-offer'))}
            >
              <RichTextContent path="floorplanshero.offer_label" as="span" />
              <span aria-hidden="true">·</span>
              <RichTextContent path="floorplanshero.offer_cta" as="span" />
            </button>
          )}
        </div>
        <div
          className="page-hero__links max-[767px]:[&&]:gap-1.5 max-[767px]:[&_.textlink]:h-11 max-[767px]:[&_.textlink]:border-muted-foreground max-[767px]:[&_.textlink]:px-3.5 max-[767px]:[&_.textlink]:py-0 max-[767px]:[&_.textlink]:text-[14px] max-[767px]:[&_.textlink]:font-medium max-[767px]:[&_.textlink]:text-[--q-ink]"
        >
          {links.map((l) => (
            <a
              key={l.href}
              className="textlink"
              href={l.href}
              onClick={() => l.href === '#compare-plans' && window.dispatchEvent(new CustomEvent('site:plans-view', { detail: 'table' }))}
            >
              <span className="max-[767px]:hidden">{l.label} ↓</span>
              <span className="hidden max-[767px]:inline">{l.short}</span>
            </a>
          ))}
        </div>
      </div>

      <div
        className={
          n
            ? 'photo page-hero__photo [&&]:h-[436px] [&&]:flex-[0_1_624px] [&.photo]:rounded-lg [&.photo]:bg-[--q-surface] max-[1199px]:[&&]:h-[360px] max-[1199px]:[&&]:flex-none max-[767px]:[&&]:h-60'
            : 'fphero__nostage'
        }
      >
        {n > 0 && (
          <div
            className="absolute inset-0 [outline:none] [&[tabindex]:focus-visible]:[outline:2px_solid_var(--q-primary-on-paper)] [&[tabindex]:focus-visible]:outline-offset-[3px]"
            tabIndex={n > 1 ? 0 : undefined}
            role={n > 1 ? 'group' : undefined}
            aria-roledescription={n > 1 ? 'carousel' : undefined}
            aria-label={n > 1 ? 'Community photos' : undefined}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') go(1);
              if (e.key === 'ArrowLeft') go(-1);
            }}
            onTouchStart={(e) => (x0.current = e.touches[0].clientX)}
            onTouchEnd={(e) => {
              if (x0.current == null || n < 2) return;
              const dx = e.changedTouches[0].clientX - x0.current;
              if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
              x0.current = null;
            }}
          >
            {active?.slot === null && slideImage(active.src, active.alt)}
            <EditableSlotGroup groupPath="floorplanshero.photo_1" definition={photo1Def} className={slotClass(0)}>
              {slide(0)}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="floorplanshero.photo_2" definition={photo2Def} className={slotClass(1)}>
              {slide(1)}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="floorplanshero.photo_3" definition={photo3Def} className={slotClass(2)}>
              {slide(2)}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="floorplanshero.photo_4" definition={photo4Def} className={slotClass(3)}>
              {slide(3)}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="floorplanshero.photo_5" definition={photo5Def} className={slotClass(4)}>
              {slide(4)}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="floorplanshero.photo_6" definition={photo6Def} className={slotClass(5)}>
              {slide(5)}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="floorplanshero.photo_7" definition={photo7Def} className={slotClass(6)}>
              {slide(6)}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="floorplanshero.photo_8" definition={photo8Def} className={slotClass(7)}>
              {slide(7)}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="floorplanshero.photo_9" definition={photo9Def} className={slotClass(8)}>
              {slide(8)}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="floorplanshero.photo_10" definition={photo10Def} className={slotClass(9)}>
              {slide(9)}
            </EditableSlotGroup>
            {n > 1 && (
              <div className="absolute bottom-4 right-4 flex items-center gap-2 max-[767px]:bottom-2.5 max-[767px]:right-2.5">
                <button className="iconbtn" type="button" aria-label="Previous photo" onClick={() => go(-1)}>←</button>
                <span className="inline-flex h-11 items-center rounded-full bg-[--q-paper] px-3.5 py-0 text-[13px] font-medium [font-variant-numeric:tabular-nums]" aria-live="polite">{cur + 1} / {n}</span>
                <button className="iconbtn" type="button" aria-label="Next photo" onClick={() => go(1)}>→</button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorPlansHero',
  displayName: 'Floor Plans Hero',
  tokens: {
    'floorplanshero.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Floor plans · <span data-type="field-token" data-field="property_name"></span></p>',
    },
    'floorplanshero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Floor plans at <span data-type="field-token" data-field="property_name"></span></p>',
      suggestedFields: ['property_floorplans_blurb', 'property_home_floorplans_blurb'],
    },
    'floorplanshero.summary': {
      label: 'Availability Line',
      description: 'Greys out while no home is available.',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_bedrooms_range"></span> bedroom homes. <span data-type="field-token" data-field="property_available_units"></span> available now.</p>',
    },
    'floorplanshero.offer_label': {
      label: 'Offer Pill Text',
      description: 'Shown only while a special offer is enabled; the pill opens the full offer.',
      type: 'richtext',
      defaultValue: '<p>Rent special on select homes</p>',
      generator: {
        prompt: 'The current special offer in a few words, without dates, fine print or exact amounts that may change, or leave empty if there is no active offer.',
        context: 'Gold pill under the Floor Plans headline, followed by "· See the Offer".',
        length: '3–6 words',
      },
    },
    'floorplanshero.offer_cta': { label: 'Offer Pill Link Text', type: 'richtext', defaultValue: '<p>See the Offer</p>' },
  },
  slotGroups: {
    'floorplanshero.photo_1': photo1Def,
    'floorplanshero.photo_2': photo2Def,
    'floorplanshero.photo_3': photo3Def,
    'floorplanshero.photo_4': photo4Def,
    'floorplanshero.photo_5': photo5Def,
    'floorplanshero.photo_6': photo6Def,
    'floorplanshero.photo_7': photo7Def,
    'floorplanshero.photo_8': photo8Def,
    'floorplanshero.photo_9': photo9Def,
    'floorplanshero.photo_10': photo10Def,
  },
};

(FloorPlansHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FloorPlansHero;
